// src/pages/Leaderboard.jsx
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import API from "../api";
import Card from "../components/ui/Card";
import PageHeader from "../components/ui/PageHeader";
import PageShell from "../components/ui/PageShell";
import ScoringGuide from "../components/ScoringGuide";
import GroupsCard from "../components/GroupsCard";
import CompareSheet from "../components/CompareSheet";
import SegmentedTabs from "../components/SegmentedTabs";
import CompetitionFilterBar from "../components/CompetitionFilterBar";
import { usePageTitle } from "../hooks/usePageTitle";
import { UserContext } from "../context/UserContext";
import {
  WC_LEADERBOARD_STAGE_OPTIONS,
  getStoredWorldCupLeaderboardStage,
  setStoredWorldCupLeaderboardStage,
} from "../constants/worldCup";
import {
  COMPETITION_OPTIONS,
  filterCompetitionsByEnabled,
  getEnabledCompetitionIds,
  pickInitialCompetitionId,
} from "../constants/competitionPrefs";
import SeasonSelect from "../components/SeasonSelect";
import { CURRENT_SEASON, SEASON_OPTIONS, formatSeasonLabel } from "../constants/season";

const getInitials = (name) => name?.slice(0, 2).toUpperCase() || "??";

const isValidPeriod = (p) => /^\d{4}-\d{2}$/.test(p);
const sortSeasonIdsDesc = (ids) => [...ids].sort((a, b) => String(b).localeCompare(String(a)));
const formatPeriodLabel = (p) => {
  if (p === "all") return "Season total";
  if (!isValidPeriod(p)) return p;
  const dt = new Date(`${p}-01T00:00:00Z`);
  return Number.isNaN(dt.getTime()) ? p : dt.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

export default function Leaderboard() {
  usePageTitle("Leaderboard");
  const { user } = useContext(UserContext);

  const enabledIds = useMemo(
    () => getEnabledCompetitionIds(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.enabled_competitions, user?.id]
  );
  const visibleLeagueTabs = useMemo(() => {
    const leagues = filterCompetitionsByEnabled(COMPETITION_OPTIONS, enabledIds).map((c) => ({
      key: c.id,
      label: c.label,
    }));
    return [...leagues, { key: "table", label: "Table Predictions" }];
  }, [enabledIds]);

  const [activeTab, setActiveTab] = useState(() => pickInitialCompetitionId(enabledIds, "premier_league"));
  const [selectedSeason, setSelectedSeason] = useState(CURRENT_SEASON);
  const [seasonOptions, setSeasonOptions] = useState(SEASON_OPTIONS);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [wcStageFilter, setWcStageFilter] = useState(getStoredWorldCupLeaderboardStage);
  const [boardView, setBoardView] = useState("everyone");
  const [leagueMeta, setLeagueMeta] = useState("Your league");
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [compareWith, setCompareWith] = useState(null);

  useEffect(() => {
    if (searchParams.get("view") === "yours") setBoardView("yours");
    const player = searchParams.get("player");
    if (player) setCompareWith(player);
  }, [searchParams]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUsername = user?.username ?? null;

  useEffect(() => {
    if (activeTab === "table") return;
    if (!enabledIds.includes(activeTab)) {
      setActiveTab(pickInitialCompetitionId(enabledIds, "premier_league"));
    }
  }, [enabledIds, activeTab]);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (activeTab === "table") {
        const res = await API.get("/table-predictions/leaderboard");
        data = res.data;
      } else {
        const params = { competition: activeTab, season: selectedSeason };
        if (activeTab === "world_cup") {
          delete params.season;
          if (wcStageFilter !== "all") params.stage = wcStageFilter;
        } else if (selectedPeriod !== "all") {
          params.period = selectedPeriod;
        }
        const res = await API.get("/leaderboard", { params });
        data = res.data;
      }
      setLeaderboard(Array.isArray(data) ? data : []);
    } catch {
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedPeriod, wcStageFilter, selectedSeason]);

  useEffect(() => {
    setSelectedPeriod("all");
    if (activeTab === "world_cup") {
      setWcStageFilter(getStoredWorldCupLeaderboardStage());
    }
    if (activeTab === "table" || activeTab === "world_cup") {
      setPeriods([]);
      return;
    }
    API.get("/leaderboard/seasons", { params: { competition: activeTab } })
      .then(({ data }) => {
        const fromApi = Array.isArray(data?.seasons) ? data.seasons : [];
        const merged = sortSeasonIdsDesc([...new Set([...fromApi, ...SEASON_OPTIONS.map((s) => s.id)])]);
        setSeasonOptions(merged.map((id) => ({ id, label: formatSeasonLabel(id) || id })));
        if (!merged.includes(selectedSeason)) {
          setSelectedSeason(merged[0] || CURRENT_SEASON);
        }
      })
      .catch(() => setSeasonOptions(SEASON_OPTIONS));

    API.get("/leaderboard/periods", { params: { competition: activeTab, season: selectedSeason } })
      .then(({ data }) => setPeriods(Array.isArray(data?.periods) ? data.periods : []))
      .catch(() => setPeriods([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSeason]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const handleWcStageChange = (stage) => {
    setWcStageFilter(stage);
    setStoredWorldCupLeaderboardStage(stage);
  };

  const periodItems = useMemo(() => {
    const valid = periods.filter(isValidPeriod).sort((a, b) => b.localeCompare(a));
    return [{ key: "all", label: "Season total" }, ...valid.map((p) => ({ key: p, label: formatPeriodLabel(p) }))];
  }, [periods]);

  const seasonItems = useMemo(
    () =>
      sortSeasonIdsDesc(seasonOptions.map((s) => s.id)).map((id) => {
        const found = seasonOptions.find((s) => s.id === id);
        return { key: id, label: found?.label || formatSeasonLabel(id) || id };
      }),
    [seasonOptions]
  );

  const showSeasonFilter = activeTab !== "table" && activeTab !== "world_cup";

  const wcStageItems = useMemo(() => WC_LEADERBOARD_STAGE_OPTIONS.map((o) => ({ key: o.id, label: o.label })), []);

  const emptyMessage =
    activeTab === "world_cup"
      ? wcStageFilter === "all"
        ? "No World Cup points yet. Predictions need finished matches to score."
        : "No points for this round yet."
      : "No points yet for this selection.";

  const scopeLabel =
    activeTab === "world_cup"
      ? wcStageFilter === "all"
        ? "Overall"
        : WC_LEADERBOARD_STAGE_OPTIONS.find((o) => o.id === wcStageFilter)?.label || wcStageFilter
      : activeTab === "table"
        ? "Final table predictions"
        : selectedPeriod === "all"
          ? formatSeasonLabel(selectedSeason)
          : formatPeriodLabel(selectedPeriod);

  const pointsOf = (u) => u.points ?? u.score ?? 0;
  const myIndex = currentUsername ? leaderboard.findIndex((u) => u.username === currentUsername) : -1;
  const me = myIndex >= 0 ? leaderboard[myIndex] : null;
  const leaderPoints = leaderboard.length ? pointsOf(leaderboard[0]) : 0;

  let meta = scopeLabel;
  if (boardView === "yours") meta = leagueMeta;
  else if (!loading && me) {
    const gap = leaderPoints - pointsOf(me);
    meta = `You're #${me.rank ?? myIndex + 1} of ${leaderboard.length} · ${
      gap > 0 ? `${gap} pts behind the leader` : "top of the table"
    }`;
  }

  const above = myIndex > 0 ? leaderboard[myIndex - 1] : null;
  const below = myIndex >= 0 && myIndex < leaderboard.length - 1 ? leaderboard[myIndex + 1] : null;
  const positionFacts = me
    ? [
        ["Rank", `#${me.rank ?? myIndex + 1}`, `of ${leaderboard.length}`],
        ["Points", pointsOf(me), scopeLabel],
        above ? ["To catch", pointsOf(above) - pointsOf(me), `pts to ${above.username}`] : null,
        below ? [above ? "Cushion" : "Lead", pointsOf(me) - pointsOf(below), `pts over ${below.username}`] : null,
      ].filter(Boolean)
    : [];

  const rail = (
    <>
      {boardView === "everyone" && !loading && me && (
        <section className="hidden lg:block rounded-xl border border-line bg-white shadow-sm overflow-hidden">
          <h2 className="px-4 py-3 border-b border-gray-100 text-[15px] font-semibold text-ink">Your position</h2>
          <dl className="grid grid-cols-2">
            {positionFacts.map(([label, value, sub], i) => (
              <div
                key={label}
                className={`px-4 py-3 min-w-0 ${i % 2 ? "border-l border-line" : ""} ${i > 1 ? "border-t border-line" : ""}`}
              >
                <dt className="text-[12px] text-muted">{label}</dt>
                <dd className="text-[22px] leading-7 font-bold tabular-nums text-ink">{value}</dd>
                <dd className="text-[12px] text-muted truncate">{sub}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <div className="hidden lg:block">
        <ScoringGuide />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-paper text-ink">
      <PageShell rail={rail} stickyRail>
        <div className="space-y-4">
        <PageHeader
          title="League"
          meta={meta}
          actions={
            <>
              {showSeasonFilter && seasonItems.length > 0 && (
                <SeasonSelect seasons={seasonItems} value={selectedSeason} onChange={setSelectedSeason} />
              )}
              <button
                type="button"
                onClick={() => {
                  if (boardView === "yours") {
                    queryClient.invalidateQueries({ queryKey: ["myGroups"] });
                    queryClient.invalidateQueries({ queryKey: ["groupBoard"] });
                  } else {
                    loadLeaderboard();
                  }
                }}
                aria-label="Refresh"
                className="flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-white text-gray-600 hover:border-gray-400 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </>
          }
        />

        <SegmentedTabs
          items={[
            { key: "everyone", label: "Everyone" },
            { key: "yours", label: "Your league" },
          ]}
          activeKey={boardView}
          onChange={(key) => {
            setBoardView(key);
            if (key === "yours" && activeTab === "table") {
              setActiveTab(pickInitialCompetitionId(enabledIds, "premier_league"));
            }
          }}
        />

        <CompetitionFilterBar
          items={boardView === "yours" ? visibleLeagueTabs.filter((tab) => tab.key !== "table") : visibleLeagueTabs}
          activeKey={activeTab === "table" && boardView === "yours" ? "premier_league" : activeTab}
          onChange={setActiveTab}
        >
          {activeTab === "world_cup" && (
            <div>
              <p className="text-[12px] font-medium text-muted mb-1.5">Round</p>
              <SegmentedTabs items={wcStageItems} activeKey={wcStageFilter} onChange={handleWcStageChange} />
            </div>
          )}
          {showSeasonFilter && periodItems.length > 1 && (
            <SegmentedTabs items={periodItems} activeKey={selectedPeriod} onChange={setSelectedPeriod} />
          )}
        </CompetitionFilterBar>

        {boardView === "yours" ? (
          <GroupsCard
            competition={activeTab === "table" ? "premier_league" : activeTab}
            season={selectedSeason}
            currentUsername={currentUsername}
            onMeta={setLeagueMeta}
            onCompare={(name) => {
              if (name && name !== currentUsername) setCompareWith(name);
            }}
          />
        ) : loading ? (
          <div className="rounded-xl border border-line bg-white shadow-sm divide-y divide-line animate-pulse" aria-hidden>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 h-14">
                <div className="h-4 w-6 bg-gray-200 rounded" />
                <div className="w-8 h-8 rounded-full bg-gray-200" />
                <div className="h-4 w-28 bg-gray-200 rounded" />
                <div className="ml-auto h-4 w-10 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <Card>
            <p className="text-center text-muted">{emptyMessage}</p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-line bg-white shadow-sm">
            <table className="w-full text-sm">
              <caption className="sr-only">League table</caption>
              <thead className="bg-gray-50 text-[12px] font-medium text-muted">
                <tr>
                  <th scope="col" className="h-9 w-12 px-4 text-right font-medium">#</th>
                  <th scope="col" className="h-9 px-2 text-left font-medium">Player</th>
                  <th scope="col" className="hidden h-9 w-20 px-2 text-right font-medium md:table-cell">Behind</th>
                  <th scope="col" className="h-9 w-16 px-2 text-right font-medium">Points</th>
                  <th scope="col" className="h-9 w-24 px-3 text-right font-medium"><span className="sr-only">Compare</span></th>
                </tr>
              </thead>
              <tbody>
            {me && above && (
              <tr className="border-b-2 border-brand/20 bg-brand-soft">
                <th scope="row" className="px-4 py-3 text-right tabular-nums font-semibold text-gray-700 align-middle">{me.rank ?? myIndex + 1}</th>
                <td className="px-2 py-3">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">
                      {getInitials(me.username)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink">
                        {me.username}
                        <span className="ml-2 text-[12px] font-semibold text-brand">You</span>
                      </span>
                      <span className="block truncate text-[12px] text-muted tabular-nums">
                        {pointsOf(above) - pointsOf(me)} pts behind {above.username}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="hidden px-2 py-3 text-right tabular-nums text-muted align-middle md:table-cell">
                  {leaderPoints - pointsOf(me)}
                </td>
                <td className="px-2 py-3 text-right tabular-nums font-bold text-ink align-middle">{pointsOf(me)}</td>
                <td className="px-3 py-3" />
              </tr>
            )}
            {leaderboard.map((u, i) => {
              const isMe = currentUsername != null && u.username === currentUsername;
              return (
                <tr
                  key={u.username}
                  aria-current={isMe ? "true" : undefined}
                  className={`border-t border-line ${isMe ? "bg-brand-soft" : ""}`}
                >
                  <th scope="row" className="h-14 px-4 text-right tabular-nums font-semibold text-gray-700">{u.rank ?? i + 1}</th>
                  <td className="h-14 px-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-600">
                        {getInitials(u.username)}
                      </span>
                      <span className="truncate font-medium text-ink">
                        {u.username}
                        {isMe && <span className="ml-2 text-[12px] font-semibold text-brand">You</span>}
                      </span>
                    </span>
                  </td>
                  <td className="hidden h-14 px-2 text-right tabular-nums text-muted md:table-cell">
                    {i === 0 ? "—" : leaderPoints - pointsOf(u)}
                  </td>
                  <td className="h-14 px-2 text-right tabular-nums font-bold text-ink">{pointsOf(u)}</td>
                  <td className="h-14 px-3 text-right">
                    {!isMe && (
                      <button
                        type="button"
                        onClick={() => setCompareWith(u.username)}
                        className="hidden h-11 px-2 text-[12px] font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:inline"
                      >
                        Compare
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>
        )}
        </div>
        {compareWith && (
          <CompareSheet
            username={compareWith}
            competition={activeTab === "table" || activeTab === "world_cup" ? "premier_league" : activeTab}
            season={selectedSeason}
            onClose={() => setCompareWith(null)}
          />
        )}
      </PageShell>
    </div>
  );
}
